import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormArray,
  UntypedFormBuilder,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { map, Observable, of, skipWhile, Subject, take, takeUntil } from 'rxjs';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { BulkPosConfigField } from '@interfaces/pos-item/1.0';
import { NgIf, NgFor, KeyValuePipe } from '@angular/common';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatFormField, MatLabel, MatHint, MatError } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { TxpIntegersOnlyDirective } from '../../directives/integers-only/1.0/txp-integers-only.directive';
import { MatCheckbox } from '@angular/material/checkbox';
import { ButtonComponent } from '../button/1.0/button.component';
@Component({
  selector: 'txp-pos-channel-types',
  templateUrl: './pos-channel-types.component.html',
  styleUrls: ['./pos-channel-types.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PosChannelTypesComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => PosChannelTypesComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    MatSlideToggle,
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    TxpIntegersOnlyDirective,
    MatHint,
    MatError,
    MatCheckbox,
    ButtonComponent,
    TranslateModule,
    KeyValuePipe,
  ],
})
export class PosChannelTypesComponent implements ControlValueAccessor, AsyncValidator, OnDestroy {
  @Input() formSubmitted: boolean;
  @Input() groupType: string;
  @Input() fields: BulkPosConfigField[];
  @Output() updateTypeValueEmitter = new EventEmitter<boolean>();

  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  onTouched: any;
  disabled: boolean;
  onChangeFn: any;
  form: UntypedFormArray;
  storeBulkUpdatesWrite = false;
  groupItemsCount = 0;
  updateTypeValue = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    public formBuilder: UntypedFormBuilder,
    private permissionsService: PermissionsService,
  ) {
    this.storeBulkUpdatesWrite = this.permissionsService.getPermission('store.bulk-updates:write');
    this.form = this.formBuilder.array([]);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form.disable() : this.form.enable();
  }

  writeValue(): void {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      fn(value);
    });
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (this.form?.status !== 'PENDING') {
      return of(
        this.form?.valid
          ? undefined
          : {
              invalidForm: {
                valid: false,
                message: this.translateService.instant('form.error.modifierGroupFieldsAreInvalid'),
              },
            },
      );
    } else {
      const obs = this.form?.statusChanges.pipe(
        takeUntil(this.unsubscribe),
        skipWhile((status) => status === 'PENDING'),
        take(1),
        map(() => {
          return this.form?.valid
            ? undefined
            : {
                invalidForm: {
                  valid: false,
                  message: this.translateService.instant(
                    'form.error.modifierGroupFieldsAreInvalid',
                  ),
                },
              };
        }),
      );
      return obs;
    }
  }

  addGroupItems() {
    this.groupItemsCount++;
    this.createFormGroup(this.form, this.fields);
  }

  removeGroupItem(index: number) {
    this.groupItemsCount--;
    this.form.removeAt(index);
  }

  createFormControl(field: BulkPosConfigField) {
    let controlValue = null;
    if (field.default !== undefined) {
      controlValue = field.default;
    }

    const controlValidators = [];
    if (field.required) {
      controlValidators.push(Validators.required);
    }
    if (field.min !== undefined) {
      controlValidators.push(Validators.min(field.min));
    }
    if (field.max !== undefined) {
      controlValidators.push(Validators.max(field.max));
    }

    return this.formBuilder.control(controlValue, controlValidators);
  }

  createFormGroup(formGroup: UntypedFormArray, fields: BulkPosConfigField[]) {
    const group = this.form as UntypedFormArray;
    let controls = {};

    if (fields) {
      controls = fields?.reduce((acc, field) => {
        acc[field.key] = this.createFormControl(field);
        return acc;
      }, {});
    }

    group.push(this.formBuilder.group(controls));
  }

  changeUpdateType(event: any) {
    this.updateTypeValue = event.checked;
    this.updateTypeValueEmitter.emit(this.updateTypeValue);
  }
}
